import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi} from 'api';
import {Alert, ConfirmModal, DataWrapper, Divider, InputNumberWithControls} from 'components';
import {MAX_USERS_LIMIT, MIN_USERS_LIMIT} from 'constants/billing-platform';
import {UserContext} from 'context';
import {useApiData, useIsChangedTo} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformSubscription} from 'types';
import {formatDate} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.SUBSCRIPTION;

type Props = {
    open: boolean;
    minUsers?: number;
    endOfTerm?: boolean;
    hasPaymentWarning?: boolean;
    subscription?: BillingPlatformSubscription;
    subscriptionNextPeriod?: BillingPlatformSubscription;
    onClose?: (confirm: boolean) => void;
}

export const EditSubscriptionModal: React.FC<Props> = ({
    open, minUsers = 1, hasPaymentWarning, subscription, subscriptionNextPeriod, endOfTerm, onClose
}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);

    const [quantity, setQuantity] = useState(endOfTerm ? subscriptionNextPeriod?.quantity : subscription?.quantity);
    const hasOpened = useIsChangedTo(open, true);
    const showPaymentWarning = useMemo(() =>
        hasPaymentWarning && subscription?.quantity < quantity, [hasPaymentWarning, subscription, quantity]);
    const showScheduleWarning = useMemo(() => subscription?.pauseDate && subscription?.quantity > quantity, [subscription, quantity]);
    const showUsersExceededWarning = useMemo(() => !showScheduleWarning && minUsers > quantity, [minUsers, quantity, showScheduleWarning]);

    const formatSubscriptionDate = useCallback((inputDate: Date) => formatDate(inputDate, timeZone, dateFormat), [timeZone, dateFormat]);
    const applyChangesLabel = useMemo(() => {
        if (endOfTerm) {
            return `${t(T_KEYS.EDIT_MODAL.CHANGES_APPLIED.ON_DATE)} ${formatSubscriptionDate(subscription?.nextBillingDate)}`;
        } else {
            return t(T_KEYS.EDIT_MODAL.CHANGES_APPLIED.IMMEDIATELY);
        }
    }, [endOfTerm, formatSubscriptionDate, subscription, t]);

    const handleSuccessEdit = useCallback(() => {
        onClose(true);
    }, [onClose]);

    const {send: editSubscription, loading, error, reset} = useApiData(billingPlatformApi.editSubscription, {
        onSuccess: handleSuccessEdit
    });

    const okDisabled = useMemo(() =>
        loading ||
        (!endOfTerm && quantity === subscription?.quantity) ||
        (endOfTerm && quantity === subscriptionNextPeriod?.quantity) ||
        (showPaymentWarning && !endOfTerm) ||
        showScheduleWarning,
    [loading, quantity, showPaymentWarning, showScheduleWarning, subscription, subscriptionNextPeriod, endOfTerm]);

    const handleClose = useCallback((confirm: boolean) => {
        if (confirm) {
            editSubscription(subscription.id, {quantity, endOfTerm});
        } else {
            onClose(false);
        }
    }, [quantity, editSubscription, subscription, endOfTerm, onClose]);

    useEffect(() => {
        if (hasOpened) {
            setQuantity(endOfTerm ? subscriptionNextPeriod?.quantity : subscription?.quantity);
        }
        return reset;
    }, [hasOpened, subscription, subscriptionNextPeriod, endOfTerm, reset]);

    return (
        <ConfirmModal
            fullWidth
            maxWidth='xs'
            title={t(endOfTerm ? T_KEYS.EDIT_MODAL.TITLE.NEXT : T_KEYS.EDIT_MODAL.TITLE.CURRENT)}
            open={open}
            okDisabled={okDisabled}
            onClose={handleClose}
        >
            {subscription &&
                <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                    <Alert>
                        <div>{applyChangesLabel}</div>
                        {!endOfTerm && <div>{t(T_KEYS.END_OF_TERM_WARNING)}</div>}
                    </Alert>
                    {showUsersExceededWarning &&
                        <Alert type='warning' className='tw-mt-4'>{t(T_KEYS.MIN_USERS_EXCEEDED_NEXT_PERIOD)}</Alert>}
                    {showPaymentWarning && !endOfTerm &&
                        <Alert type='warning' className='tw-mt-4'>{t(T_KEYS.PAYMENT_WARNING)}</Alert>}
                    {showScheduleWarning &&
                        <Alert type='warning' className='tw-mt-4'>{t(T_KEYS.SCHEDULE_WARNING)}</Alert>}
                    <div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-mt-8'>
                        <div>{t(T_KEYS.EDIT_MODAL.CURRENT_MAX_USERS)}</div>
                        <div className='tw-text-center tw-mr-6'>{subscription.quantity}</div>
                        <div className='tw-font-bold tw-flex tw-items-center'>{t(T_KEYS.EDIT_MODAL.NEW_MAX_USERS)}</div>
                        <InputNumberWithControls
                            value={quantity || 0}
                            min={endOfTerm ? MIN_USERS_LIMIT : subscription.quantity}
                            max={MAX_USERS_LIMIT}
                            className='tw-justify-center'
                            onChange={setQuantity}
                        />
                    </div>
                    <Divider className='tw-my-4' />
                    <div className='tw-flex tw-justify-between'>
                        <div>{t(T_KEYS.PRICE)}</div>
                        <div className='tw-font-bold'>
                            {`${quantity * subscription.unitPrice} ${subscription.currency} / ${subscription.billingPeriodUnit}`}
                        </div>
                    </div>
                    {error && <Alert type='danger' className='tw-mt-8'>{t(T_KEYS.EDIT_MODAL.ERROR)}</Alert>}
                </DataWrapper>}
        </ConfirmModal>
    );
};